import { getAutoTagVersion } from "@/lib/auto-tag-version";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { Dimension, MemberSearchResponseType } from "@/types/mapping";
import { AxiosResponse } from "axios";
import axiosInstance from "../axios-instance";
import { ApiError } from "../errors";
import {
  AssignCellRolesRequest,
  AssignCellRolesResponse,
  DragTagToCellsRequest,
  DragTagToCellsResponse,
  GetCellAutoTagInfoResponse,
  GetCellsListResponse,
  GetMappingGuideResponse,
  GetTableNotUsedSumTagKeysResponse,
  GetTagsFromCellResponse,
  PatchSearchCacheRequest,
  PatchTableNotUsedSumTagKeysRequest,
  PatchUpdateCellInfoRequest,
  PeriodDragTaggingToCellsRequest,
  PeriodDragTaggingToCellsResponse,
  RemoveTagFromCellsRequest,
  RemoveTagFromCellsResponse,
  TableInfo,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeTagInfo = (tag: any) => ({
  ...tag,
  roleKey: tag.roleKey ?? tag.role?.key ?? "",
  role: undefined,
});

export const getTagsFromCell = async (
  workbookId: string,
  tableId: string,
  cellAddress?: string | null,
): Promise<GetTagsFromCellResponse> => {
  if (!cellAddress) {
    throw Error("cellAddress is required");
  }
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}/tags`,
  );
  const data = response.data;
  return {
    ...data,
    tags: data.tags.map(normalizeTagInfo),
  };
};

export const getPeriodFromCell = async (
  workbookId: string,
  tableId: string,
  cellAddress?: string | null,
): Promise<GetTagsFromCellResponse> => {
  if (!cellAddress) {
    throw Error("cellAddress is required");
  }
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}/tags`,
  );
  const data = response.data;
  return {
    ...data,
    tags: data.tags.map(normalizeTagInfo),
  };
};

export const searchFromCell = async (
  workbookId: string,
  tableId: string,
  dimension: Dimension,
  cellAddress: string,
): Promise<MemberSearchResponseType | null> => {
  try {
    const response = await axiosInstance.get(
      `/v1/workbooks/${workbookId}/tables/${tableId}/taxonomy/tags`,
      {
        params: {
          address: cellAddress,
          dimension,
        },
      },
    );

    return response.data;
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.errorCode === "UNPROCESSABLE_CELL_ERROR"
    ) {
      return null;
    }
    throw error;
  }
};

export const getCellsList = async (
  workbookId: string,
  tableId: string,
): Promise<GetCellsListResponse> => {
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells`,
  );

  return response.data as GetCellsListResponse;
};

export const getCellAutoTagInfo = async (
  workbookId: string,
  tableId: string,
  cellAddress?: string | null,
): Promise<GetCellAutoTagInfoResponse> => {
  if (!cellAddress) {
    throw Error("cellAddress is required");
  }
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}/auto-tag-info`,
  );

  return response.data as GetCellAutoTagInfoResponse;
};

export const putTagToCell = async ({
  workbookId,
  tableId,
  cellAddress,
  lineItemTag,
  memberTags,
  roleKey,
  accountingPeriod,
  targetYear,
  accumulated,
}: {
  workbookId: string;
  tableId: string;
  cellAddress: string;
  lineItemTag: string | null;
  memberTags: string[];
  roleKey: string | null;
  accountingPeriod: AccountingPeriod | null;
  targetYear: TargetYear | null;
  accumulated: Accumulated | null;
}) => {
  const response = await axiosInstance.put(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}`,
    {
      roleKey,
      period: accountingPeriod,
      targetYear,
      accumulated,
      lineItem: lineItemTag ? { tagKey: lineItemTag } : null,
      members: memberTags.map((tagKey) => ({ tagKey })),
    },
  );

  return response.data;
};

export const dragTagToCells = async ({
  workbookId,
  tableId,
  request,
}: {
  workbookId: string;
  tableId: string;
  request: DragTagToCellsRequest;
}): Promise<DragTagToCellsResponse> => {
  try {
    const response = await axiosInstance.patch(
      `/v1/workbooks/${workbookId}/tables/${tableId}/cells`,
      request,
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      if (error.statusCode === 400 || error.statusCode === 409) {
        throw new Error("셀을 정확하게 드래그해주세요.");
      }
      if (error.statusCode === 404) {
        throw new Error("테이블을 찾을 수 없습니다.");
      }
      throw new Error(error.message || "태그 추가 중 오류가 발생했습니다.");
    }
    throw new Error("태그 추가 중 오류가 발생했습니다.");
  }
};

export const periodDragTaggingToCells = async ({
  workbookId,
  tableId,
  request,
}: {
  workbookId: string;
  tableId: string;
  request: PeriodDragTaggingToCellsRequest;
}): Promise<PeriodDragTaggingToCellsResponse> => {
  try {
    const response = await axiosInstance.patch(
      `/v1/workbooks/${workbookId}/tables/${tableId}/drag-tag-period`,
      request,
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      if (error.statusCode === 400 || error.statusCode === 409) {
        throw new Error("셀을 정확하게 드래그해주세요.");
      }
      if (error.statusCode === 404) {
        throw new Error("워크북 또는 테이블을 찾을 수 없습니다.");
      }
      throw new Error(
        error.message || "기간 속성 변경 중 오류가 발생했습니다.",
      );
    }
    throw new Error("기간 속성 변경 중 오류가 발생했습니다.");
  }
};

export const assignCellRoles = async (
  workbookId: string,
  tableId: string,
  request: AssignCellRolesRequest,
): Promise<AssignCellRolesResponse> => {
  const response = await axiosInstance.patch(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/role-assignment`,
    request,
  );

  return response.data;
};

export const patchSearchCache = async (
  workbookId: string,
  tableId: string,
  request: PatchSearchCacheRequest,
) => {
  const response = await axiosInstance.patch(
    `/v1/workbooks/${workbookId}/tables/${tableId}/taxonomy/tags`,
    request,
  );

  return response.data;
};

export const clearTableCellsTags = async (
  workbookId: string,
  tableId: string,
) => {
  await axiosInstance.patch(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/clear-tags`,
  );
};

export const removeTagFromCells = async (
  workbookId: string,
  tableId: string,
  request: RemoveTagFromCellsRequest,
): Promise<RemoveTagFromCellsResponse> => {
  const response = await axiosInstance.patch(
    `/v1/workbooks/${workbookId}/tables/${tableId}/remove-tag-from-cells`,
    request,
  );
  return response.data as RemoveTagFromCellsResponse;
};

export const getTableInfo = async (
  workbookId: string,
  tableId: string,
): Promise<TableInfo> => {
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}`,
  );

  return response.data;
};

export const getMappingGuideWithRecommendedTags = async (
  workbookId: string,
  tableId: string,
  tagKey: string,
  cellAddress: string,
) => {
  const response: AxiosResponse<GetMappingGuideResponse> =
    await axiosInstance.get(
      `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}/tags/${tagKey}/mapping-guide`,
    );

  return response.data;
};

export const patchUpdateCellInfo = async (
  workbookId: string,
  tableId: string,
  cellAddress: string,
  request: PatchUpdateCellInfoRequest,
) => {
  const response = await axiosInstance.patch(
    `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${cellAddress}`,
    request,
  );

  return response.data;
};

export const patchTableNotUsedSumTagKeys = async (
  workbookId: string,
  tableId: string,
  request: PatchTableNotUsedSumTagKeysRequest,
) => {
  const response = await axiosInstance.patch(
    `/v2/workbooks/${workbookId}/tables/${tableId}/not-used-sum-tag-keys`,
    request,
  );
  return response.data;
};

export const getTableNotUsedSumTagKeys = async (
  workbookId: string,
  tableId: string,
) => {
  const response = await axiosInstance.get(
    `/v2/workbooks/${workbookId}/tables/${tableId}/not-used-sum-tag-keys`,
  );
  return response.data as GetTableNotUsedSumTagKeysResponse;
};

export const autoBuildHierarchy = async (
  workbookId: string,
  tableId: string,
  orderOnly = false,
) => {
  const response = await axiosInstance.put(
    `/v2/workbooks/${workbookId}/tables/${tableId}/auto-building`,
    undefined,
    orderOnly ? { params: { order_only: true } } : undefined,
  );
  return response.data;
};

export const resetTags = async (workbookId: string, tableId: string) => {
  const response = await axiosInstance.patch(
    `/v2/workbooks/${workbookId}/tables/${tableId}/reset-tags`,
  );
  return response.data;
};

export interface AutoTagEnqueuedResponse {
  enqueued: number;
}

// 실험실(/settings)에서 고른 오토태깅 버전. "latest"(기본)면 필드를 보내지 않는다.
const autoTagVersionBody = () => {
  const version = getAutoTagVersion();
  return version === "latest" ? {} : { version };
};

/** 단일 표 오토태깅을 백그라운드로 enqueue (202). 진행상황은 status 폴링으로 본다. */
export const enqueueAutoTagTable = async (
  workbookId: string,
  tableId: string,
): Promise<AutoTagEnqueuedResponse> => {
  const response = await axiosInstance.post(
    `/v1/workbooks/${workbookId}/tables/${tableId}/auto-tag`,
    autoTagVersionBody(),
  );
  return response.data as AutoTagEnqueuedResponse;
};

/** 워크북 전체 오토태깅을 표당 Cloud Task 로 enqueue (202). */
export const enqueueAutoTagWorkbook = async (
  workbookId: string,
): Promise<AutoTagEnqueuedResponse> => {
  const response = await axiosInstance.post(
    `/v1/workbooks/${workbookId}/auto-tag`,
    autoTagVersionBody(),
  );
  return response.data as AutoTagEnqueuedResponse;
};

/** 선택한 표들만 오토태깅 enqueue (202). taggable 이 아닌 표는 서버가 무시한다. */
export const enqueueAutoTagTables = async (
  workbookId: string,
  tableIds: string[],
): Promise<AutoTagEnqueuedResponse> => {
  const response = await axiosInstance.post(
    `/v1/workbooks/${workbookId}/auto-tag`,
    { tableIds, ...autoTagVersionBody() },
  );
  return response.data as AutoTagEnqueuedResponse;
};

export type AutoTagTaskStatusValue =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED";

export type AutoTagOutcome =
  | "SUCCESS"
  | "EMPTY"
  | "SKIPPED"
  | "ERROR"
  | "ALREADY_TAGGED"
  | "AXIS_FALLBACK"
  | "NEEDS_REVIEW";

export interface AutoTagTableStatus {
  tableId: string;
  tableName: string;
  /** null = 태스크가 생성된 적 없는 표(미실행). */
  status: AutoTagTaskStatusValue | null;
  outcome: AutoTagOutcome | null;
  currentTool: string | null;
  toolCallCount: number;
  errorMessage: string | null;
  /** false 면 일반사항·회계정책·추정 등의 목차(1~3번의 일반사항·회계정책·추정)의 표 — 목록엔 보이되 실행되지
   * 않는다. 체크박스를 잠그고 전체 선택·진행률 분모에서 뺀다. */
  taggingTarget: boolean;
}

export interface AutoTagStatusResponse {
  tables: AutoTagTableStatus[];
}

/** 워크북 오토태깅 진행 상태(폴링). */
export const getAutoTagStatus = async (
  workbookId: string,
): Promise<AutoTagStatusResponse> => {
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/auto-tag/status`,
  );
  return response.data as AutoTagStatusResponse;
};

export type AutoTagEventKind =
  | "tool_call"
  | "references"
  | "llm_response"
  | "conversation"
  | "warning";

export interface AutoTagToolCall {
  seq: number;
  kind: AutoTagEventKind;
  tool: string;
  request: Record<string, unknown>;
  response: Record<string, unknown>;
  ok: boolean;
  durationMs: number;
}

export interface AutoTagToolCallsResponse {
  tableId: string;
  calls: AutoTagToolCall[];
}

/** 표별 tool 호출 로그(아코디언 펼침 시 on-demand, since 이후 증분). */
export const getAutoTagToolCalls = async (
  workbookId: string,
  tableId: string,
  since = 0,
): Promise<AutoTagToolCallsResponse> => {
  const response = await axiosInstance.get(
    `/v1/workbooks/${workbookId}/tables/${tableId}/auto-tag/tool-calls`,
    { params: { since } },
  );
  return response.data as AutoTagToolCallsResponse;
};
